import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@src/components/Button/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@src/components/shad/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/components/shad/popover";

export type ComboboxItem = {
  value: string;
  render: React.ReactNode;
  searchText: string;
};

interface ComboboxProps {
  items: ComboboxItem[];
  placeholder?: string;
  emptyMessage?: string;
  onChange?: (value: string) => void;
  triggerClassName?: string;
  defaultValue?: string;
}

export function Combobox({
  items,
  placeholder = "Select an item...",
  emptyMessage = "No items found.",
  onChange,
  triggerClassName,
  defaultValue,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredItems = items.filter((item) =>
    item.searchText.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedItem = items.find((item) => item.value === value);
  const renderDefaultValue = items.find((item) => item.value === defaultValue);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="plain"
          role="combobox"
          aria-expanded={open}
          className={cn(triggerClassName, "w-full justify-between")}
        >
          {value ? (
            <span className="flex items-center truncate w-full">
              {selectedItem?.render}
            </span>
          ) : defaultValue ? (
            <span className="flex items-center truncate w-full">
              {renderDefaultValue?.render}
            </span>
          ) : (
            placeholder
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)]  p-0">
        <Command>
          <CommandInput
            placeholder="Search..."
            onValueChange={(search) => setSearchQuery(search)}
          />
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandGroup>
            {filteredItems?.map((item, index) => (
              <CommandItem
                key={index}
                value={`${item?.value}|${item.searchText}`}
                onSelect={(currentValue) => {
                  setValue(
                    currentValue.split("|")[0] === value
                      ? ""
                      : currentValue.split("|")[0]
                  );
                  onChange?.(
                    currentValue.split("|")[0] === value
                      ? ""
                      : currentValue.split("|")[0]
                  );
                  setOpen(false);
                }}
              >
                <div className="flex items-center w-full">
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 flex-shrink-0",
                      value.split("|")[0] === item.value
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  {item.render}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
